// Vertical timeline: one node per phase with state-driven icon + label.
// Source of truth for screen-reader users (the cardiograph is decorative).
//
// Phase order is dynamic — `cleaning_volumes` only appears when the
// operation has clean_volumes=true.

import { Box, Collapse, Group, Stack, Text, ThemeIcon, Timeline, UnstyledButton } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  IconAlertTriangle,
  IconChevronRight,
  IconCircleCheck,
  IconCircleX,
  IconClock,
  IconDownload,
  IconHeartbeat,
  IconLoader2,
  IconPlayerStop,
  IconRotate,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react'
import { motion } from 'framer-motion'
import { useMemo } from 'react'
import { useSeguirOFim } from '@/hooks/useSeguirOFim'
import { COLOR, MOTION } from './tokens'
import { useTicker } from './useTicker'
import { PHASE_LABELS, PHASE_ORDER } from './types'
import type { DockerOperation, PhaseEntry, PhaseName } from './types'

const PHASE_ICONS: Record<PhaseName, React.ComponentType<any>> = {
  inspecting: IconSearch,
  stopping: IconPlayerStop,
  cleaning_volumes: IconTrash,
  pulling: IconDownload,
  recreating: IconRotate,
  awaiting_healthy: IconHeartbeat,
}

interface DisplayPhase {
  name: PhaseName
  status: 'pending' | 'running' | 'success' | 'failed'
  startedAt?: string
  endedAt?: string
  output?: string
  error?: string
  /** When true, the phase succeeded but with a non-fatal degradation
   *  (e.g. pull skipped because image not in registry). */
  warning?: boolean
}

function buildDisplayPhases(
  op: DockerOperation,
  customOrder?: readonly PhaseName[],
): DisplayPhase[] {
  let order: readonly PhaseName[]
  if (customOrder) {
    order = customOrder
  } else if (op.clean_volumes) {
    order = PHASE_ORDER
  } else {
    order = PHASE_ORDER.filter(n => n !== 'cleaning_volumes')
  }

  const byName = new Map<PhaseName, PhaseEntry>()
  for (const e of op.phases) byName.set(e.name, e)

  return order.map((name): DisplayPhase => {
    const entry = byName.get(name)
    if (!entry) {
      return { name, status: 'pending' }
    }
    const meta = entry.metadata ?? {}
    return {
      name,
      status: entry.status,
      startedAt: entry.started_at,
      endedAt: entry.ended_at,
      output: entry.output,
      error: entry.error,
      warning: meta['pull_skipped'] === true || meta['used_local_image'] === true,
    }
  })
}

function durationLabel(startedAt?: string, endedAt?: string): string | null {
  if (!startedAt) return null
  const end = endedAt ? new Date(endedAt) : new Date()
  const start = new Date(startedAt)
  const ms = end.getTime() - start.getTime()
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  const m = Math.floor(ms / 60_000)
  const s = Math.floor((ms % 60_000) / 1000)
  return `${m}m ${s.toString().padStart(2, '0')}s`
}

interface Props {
  operation: DockerOperation
  /** Override the phase order. When provided, takes precedence over the
   *  auto-derived hard-restart order. Used for deploy operations. */
  phaseOrder?: readonly PhaseName[]
}

export function PhaseTimeline({ operation, phaseOrder }: Props) {
  const phases = useMemo(
    () => buildDisplayPhases(operation, phaseOrder),
    [operation, phaseOrder],
  )
  const activeIndex = phases.findIndex(p => p.status === 'running')
  // Re-render every 1s so the duration label of the active phase ticks
  // forward smoothly (otherwise it'd only update on the polling cadence).
  useTicker(activeIndex >= 0 ? 1000 : 60_000)

  return (
    <Timeline
      active={activeIndex >= 0 ? activeIndex : phases.findIndex(p => p.status === 'pending')}
      bulletSize={28}
      lineWidth={2}
      role="list"
      aria-label="Fases do hard-restart"
      styles={{
        itemTitle: { fontWeight: 600, fontFamily: '"Barlow", sans-serif' },
      }}
    >
      {phases.map((phase, i) => (
        <Timeline.Item
          key={phase.name}
          bullet={<PhaseBullet phase={phase} />}
          title={
            <Group gap={8} justify="space-between" wrap="nowrap">
              <Text size="sm" fw={600} role="listitem">
                {PHASE_LABELS[phase.name]}
              </Text>
              <Group gap={6} wrap="nowrap">
                {phase.startedAt && (
                  <Text
                    size="xs"
                    c="dimmed"
                    style={{ fontFamily: '"JetBrains Mono", monospace' }}
                  >
                    {durationLabel(phase.startedAt, phase.endedAt)}
                  </Text>
                )}
              </Group>
            </Group>
          }
        >
          <PhaseDetails phase={phase} index={i} />
        </Timeline.Item>
      ))}
    </Timeline>
  )
}

function PhaseBullet({ phase }: { phase: DisplayPhase }) {
  if (phase.status === 'success') {
    if (phase.warning) {
      return (
        <ThemeIcon size={28} radius="xl" color="yellow" variant="filled" title="Concluído com aviso">
          <IconAlertTriangle size={16} />
        </ThemeIcon>
      )
    }
    return (
      <ThemeIcon size={28} radius="xl" color="inovareTeal" variant="filled">
        <IconCircleCheck size={16} />
      </ThemeIcon>
    )
  }
  if (phase.status === 'failed') {
    return (
      <ThemeIcon size={28} radius="xl" color="red" variant="filled">
        <IconCircleX size={16} />
      </ThemeIcon>
    )
  }
  if (phase.status === 'running') {
    const Icon = PHASE_ICONS[phase.name]
    return (
      <ThemeIcon
        size={28}
        radius="xl"
        variant="filled"
        color="inovareTeal"
        style={{
          boxShadow: `0 0 0 4px ${COLOR.glow}`,
        }}
      >
        <motion.span
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          style={{ display: 'inline-flex' }}
        >
          {phase.name === 'awaiting_healthy' ? (
            <Icon size={16} />
          ) : (
            <IconLoader2 size={16} />
          )}
        </motion.span>
      </ThemeIcon>
    )
  }
  // pending
  const Icon = PHASE_ICONS[phase.name]
  return (
    <ThemeIcon size={28} radius="xl" color="gray" variant="light">
      <Icon size={16} />
    </ThemeIcon>
  )
}

function PhaseDetails({ phase, index }: { phase: DisplayPhase; index: number }) {
  const [opened, { toggle }] = useDisclosure(false)
  const hasOutput = !!phase.output || !!phase.error
  const texto = (phase.error ?? phase.output ?? '').trim()
  // Output que cresce rola para o fim, como num terminal (consoleai#25060).
  const seguirOFim = useSeguirOFim<HTMLPreElement>(texto)

  if (phase.status === 'pending') {
    return (
      <Text size="xs" c="dimmed" pl={4} style={{ fontStyle: 'italic' }}>
        <IconClock
          size={11}
          style={{ verticalAlign: 'middle', marginRight: 4, opacity: 0.5 }}
        />
        aguardando…
      </Text>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ ...MOTION.phaseEntrance, delay: Math.min(0.3, index * MOTION.phaseStagger) }}
    >
      {phase.error && (
        <Text size="xs" c="red.7" pl={4} mb={4}>
          {phase.error}
        </Text>
      )}
      {hasOutput && (
        <>
          <UnstyledButton
            onClick={toggle}
            aria-expanded={opened}
            aria-label={opened ? 'Recolher output' : 'Expandir output'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px 4px 4px',
              borderRadius: 6,
              transition: 'background 120ms ease',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(2, 84, 104, 0.06)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <motion.span
              animate={{ rotate: opened ? 90 : 0 }}
              transition={{ duration: 0.18 }}
              style={{ display: 'inline-flex', color: 'var(--ai-text-secondary, #5f7882)' }}
            >
              <IconChevronRight size={14} />
            </motion.span>
            <Text size="sm" c="dimmed" fw={500}>
              {opened ? 'Ocultar output' : 'Ver output'}
            </Text>
          </UnstyledButton>
          <Collapse in={opened}>
            <Box
              component="pre"
              ref={seguirOFim}
              mt={6}
              p={10}
              style={{
                background: COLOR.surfaceDark,
                color: '#c9d1d9',
                borderRadius: 6,
                fontSize: 11,
                fontFamily: '"JetBrains Mono", monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                maxHeight: 240,
                overflowY: 'auto',
              }}
            >
              {texto || '(sem output)'}
            </Box>
          </Collapse>
        </>
      )}
    </motion.div>
  )
}
